import type { DeviceNativeStatus } from './types.js';

/**
 * First shell allowed to pin: edits only its own saved entry in place, proves the pin can be released
 * before setting it, and releases a pin whose AP stays unreachable for 5 minutes. 0.5.12 deleted and
 * re-saved entries; 0.5.13 had no stranded-AP release; 0.5.14 did not prove the release first.
 */
export const WIFI_PIN_MIN_APP_VERSION = '0.5.15';

const WIFI_LEVELS = ['매우 약함', '약함', '보통', '양호', '강함'] as const;

/** Android shell >= 0.5.10. One line shared by the on-panel status and the admin device page. */
export function formatNetworkStatus(status: DeviceNativeStatus | null | undefined): string | null {
  if (!status?.networkType) return null;
  if (status.networkType === 'ethernet') return '유선 LAN';
  if (status.networkType === 'none') return '연결 없음';
  if (typeof status.wifiRssi !== 'number') return status.networkType === 'wifi' ? 'Wi-Fi · 신호 확인 불가' : '기타 네트워크';
  const level = WIFI_LEVELS[Math.max(0, Math.min(WIFI_LEVELS.length - 1, status.wifiLevel ?? 0))];
  const band = typeof status.wifiFrequencyMhz === 'number' ? (status.wifiFrequencyMhz >= 5900 ? '6GHz' : status.wifiFrequencyMhz >= 4900 ? '5GHz' : '2.4GHz') : null;
  return ['Wi-Fi', status.wifiSsid ?? null, `${status.wifiRssi} dBm (${level})`, band,
    typeof status.wifiLinkMbps === 'number' ? `${status.wifiLinkMbps}Mbps` : null].filter(Boolean).join(' · ');
}

/** True when `version` (x.y.z) is at least `minimum`. Anything unparsable is treated as older. */
export function appVersionAtLeast(version: string | null | undefined, minimum: string): boolean {
  const parse = (value: string | null | undefined) => /^(\d+)\.(\d+)\.(\d+)$/.exec(value ?? '')?.slice(1).map(Number) ?? null;
  const have = parse(version), need = parse(minimum);
  if (!have || !need) return false;
  for (let i = 0; i < 3; i++) if (have[i] !== need[i]) return have[i]! > need[i]!;
  return true;
}

/** Why the operator cannot pin this panel's Wi-Fi now, or null. Shared by the API guard and the admin card. */
export function wifiPinBlockReason(device: { appVersion?: string | null; nativeStatus?: DeviceNativeStatus | null }): string | null {
  if (!appVersionAtLeast(device.appVersion, WIFI_PIN_MIN_APP_VERSION)) return `AP 고정은 APK ${WIFI_PIN_MIN_APP_VERSION} 이상에서 사용할 수 있습니다.`;
  const mode = device.nativeStatus?.wifiPinMode;
  if (mode === 'own') return null;
  if (!mode) return '고정 가능 여부를 확인하는 중입니다 · 상태 즉시 확인을 눌러 주세요.';
  return `원격 고정 불가${device.nativeStatus?.wifiPinProbe ? ` (${device.nativeStatus.wifiPinProbe})` : ''} · 현장에서 BRICK Lit 앱의 Wi-Fi 연결로 다시 연결하면 가능합니다.`;
}
